<script setup lang="ts">
import { computed, ref } from 'vue';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { SeriesRanking } from '@/composables/records/useGroupedWeeklyAggregation';

/*
 * A grouped weekly chart's own multi-select: every series in the fetched period, ranked by total,
 * with a checkbox per series. The trigger's own label already says how many of how many are
 * checked - the page words it - so nothing here restates the count.
 */
const props = defineProps<{
    ranking: SeriesRanking[];
    selectedIds: Set<number>;
    label: string;
}>();

const emit = defineEmits<{
    toggle: [id: number];
}>();

const search = ref('');

const visibleRanking = computed(() => {
    const term = search.value.trim().toLowerCase();

    return term ? props.ranking.filter((entry) => entry.name.toLowerCase().includes(term)) : props.ranking;
});
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <Button variant="outline" size="sm" class="font-medium data-[state=open]:bg-gray-100">{{ label }}</Button>
        </PopoverTrigger>
        <PopoverContent align="end" class="p-0 w-64">
            <div class="p-2 border-b">
                <Input v-model="search" :placeholder="$t('global.uiElements.dataTable.filter.texts.search')" class="h-9" />
            </div>
            <ScrollArea class="max-h-64">
                <div class="p-1">
                    <label v-for="entry in visibleRanking" :key="entry.id" class="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-100">
                        <Checkbox :checked="selectedIds.has(entry.id)" @update:checked="emit('toggle', entry.id)" />
                        <span class="truncate">{{ entry.name }}</span>
                    </label>
                    <div v-if="!visibleRanking.length" class="px-2 py-3 text-center text-gray-500">{{ $t('global.uiElements.dataTable.filter.texts.noOptions') }}</div>
                </div>
            </ScrollArea>
        </PopoverContent>
    </Popover>
</template>
